import { Controller } from "@hotwired/stimulus"
import { vapidApplicationServerKey } from "../utils/push_vapid"

// Contextual push-enrollment nudge (#218). Hidden by default; reveals itself
// only when this device could subscribe but hasn't: permission still at
// "default", no local subscription, no subscription on the server (any
// device), not recently dismissed. If permission was already granted we never
// ask again — a missing subscription is re-created silently and synced. The
// browser permission prompt fires from the button click, never on page load.
const DISMISS_KEY = "jane.push_prompt.dismissed_at"
const DISMISS_DAYS = 30

export default class extends Controller {
    static targets = ["card"]
    static values = { subscribed: Boolean }

    async connect() {
        if (document.body.classList.contains("turbo-native")) return
        if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return
        if (Notification.permission === "denied") return
        if (!this._vapidKey()) return

        try {
            this.registration = await navigator.serviceWorker.register("/service_worker.js")
            const subscription = await this.registration.pushManager.getSubscription()
            if (subscription) {
                // Make sure the server knows this endpoint (heals a lost row).
                await this._sync(subscription)
                return
            }
            if (Notification.permission === "granted") {
                // The user already said yes on this device; the subscription
                // just went missing (eviction, cleared data, earlier failure).
                // Re-subscribe silently instead of asking again.
                await this._subscribe()
                return
            }
        } catch (e) {
            return
        }

        if (this.subscribedValue) return
        if (this._recentlyDismissed()) return
        if (this.hasCardTarget) this.cardTarget.hidden = false
    }

    async enable() {
        // Snooze up front so a silently failing subscribe can't re-ask on
        // every page load.
        this._snooze()
        try {
            const permission = await Notification.requestPermission()
            if (permission === "granted") await this._subscribe()
        } catch (e) {}
        this._hide()
    }

    dismiss() {
        this._snooze()
        this._hide()
    }

    async _subscribe() {
        const subscription = await this.registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: vapidApplicationServerKey(this._vapidKey())
        })
        await this._sync(subscription)
    }

    async _sync(subscription) {
        const json = subscription.toJSON()
        if (!json.endpoint || !json.keys) return
        await fetch("/push_subscription", {
            method: "POST",
            headers: this._headers(),
            body: JSON.stringify({ subscription: { endpoint: json.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth } })
        })
    }

    _hide() {
        if (this.hasCardTarget) this.cardTarget.hidden = true
    }

    _snooze() {
        try {
            localStorage.setItem(DISMISS_KEY, String(Date.now()))
        } catch (e) {}
    }

    _recentlyDismissed() {
        try {
            const at = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10)
            return at && Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000
        } catch (e) {
            return false
        }
    }

    _vapidKey() {
        const meta = document.querySelector("meta[name='vapid-public-key']")
        return meta && meta.content ? meta.content : null
    }

    _headers() {
        const token = document.querySelector("meta[name='csrf-token']")
        return { "Content-Type": "application/json", "X-CSRF-Token": token ? token.content : "" }
    }
}
